import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, X } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, logEvent, logException } from "@um/ui";
import type { FeedbackReportCategory } from "@um/contracts";
import { useReportPageStore } from "@/store/useReportPageStore";
import { submitPageReport } from "@/services/feedback";

/**
 * Slide-up drawer for reporting the current page or a specific piece of
 * AI-generated content. Opened via `useReportPageStore.openReport()` (which
 * captures the screenshot first); mounted once at the app root so it's
 * available on every route — Google Play's AI-Generated Content policy
 * requires reporting to work without leaving the app.
 */
const REPORT_CATEGORIES: { value: FeedbackReportCategory; label: string }[] = [
  { value: "offensive", label: "Offensive or inappropriate" },
  { value: "sexual", label: "Sexual content" },
  { value: "violent", label: "Violent or scary" },
  { value: "harmful", label: "Harmful or unsafe" },
  { value: "wrong", label: "Something looks wrong" },
  { value: "other", label: "Other" },
];

/**
 * Categories that indicate the content itself is unsafe. For these (and only
 * these) the drawer offers to also remove the reported content — "Something
 * looks wrong" / "Other" are quality reports where deleting would be data
 * loss, not moderation.
 */
const SAFETY_CATEGORIES: FeedbackReportCategory[] = [
  "offensive",
  "sexual",
  "violent",
  "harmful",
];

export function ReportPageDrawer() {
  const { accountId } = useAuth();
  const {
    isOpen,
    screenshot,
    pagePath,
    pageTitle,
    content,
    removeAction,
    close,
    removeScreenshot,
  } = useReportPageStore();
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<FeedbackReportCategory | null>(
    null
  );
  const [removeRequested, setRemoveRequested] = useState(true);
  const [sending, setSending] = useState(false);

  const canSubmit = !!accountId && (!!category || !!message.trim());
  // Offer removal only when the surface supplied a handler AND the chosen
  // category says the content is unsafe. Default-on: after reporting
  // offensive content the user expects it to go away.
  const offerRemoval =
    !!removeAction && !!category && SAFETY_CATEGORIES.includes(category);

  const resetForm = () => {
    setMessage("");
    setCategory(null);
    setRemoveRequested(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
      resetForm();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || sending) return;

    setSending(true);
    try {
      await submitPageReport(accountId!, {
        message,
        category: category ?? undefined,
        content: content ?? undefined,
        pagePath,
        pageTitle,
        screenshot,
      });

      // Removal runs after the report so the report (with screenshot) is
      // preserved even when the content is taken down.
      let removed = false;
      let removalFailed = false;
      if (offerRemoval && removeRequested && removeAction) {
        try {
          await removeAction();
          removed = true;
          logEvent("um_report_content_removed", {
            path: pagePath,
            contentType: content?.type,
            category,
          });
        } catch (error) {
          removalFailed = true;
          logException(error);
          console.error(
            "[reportPage] Report sent but content removal failed",
            error
          );
        }
      }

      if (removed) {
        toast.success(
          "Thanks! Your report was sent and the content was removed from your app."
        );
      } else if (removalFailed) {
        toast.error(
          "Your report was sent, but we couldn't remove the content. Please try deleting it manually."
        );
      } else {
        toast.success("Thanks! Your report has been sent to our team.");
      }
      close();
      resetForm();
    } catch (error) {
      logException(error);
      console.error("[reportPage] Failed to submit report", error);
      toast.error("Couldn't send your report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reportedName = content?.title || pageTitle;

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-col overflow-y-auto"
        >
          <DrawerHeader>
            <DrawerTitle>
              {content ? "Report this content" : "Report this page"}
            </DrawerTitle>
            <DrawerDescription>
              {content
                ? `See something offensive or inappropriate${
                    reportedName ? ` about “${reportedName}”` : ""
                  }? Let us know — we review every report and use them to keep content safe.`
                : `Something off${
                    reportedName ? ` on “${reportedName}”` : ""
                  }? Tell us the good and the bad — we read every report.`}
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex flex-col gap-3 px-4">
            <div className="flex flex-wrap gap-2">
              {REPORT_CATEGORIES.map((option) => {
                const selected = category === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setCategory(selected ? null : option.value)
                    }
                    aria-pressed={selected}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      selected
                        ? "border-purple-600 bg-purple-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {offerRemoval && (
              <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <Checkbox
                  checked={removeRequested}
                  onCheckedChange={(checked) =>
                    setRemoveRequested(checked === true)
                  }
                  className="mt-0.5"
                />
                <span className="text-sm leading-snug text-gray-700">
                  Also remove {content?.title ? `“${content.title}”` : "this"}{" "}
                  from my app
                </span>
              </label>
            )}

            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                content
                  ? "Add details (optional)"
                  : "What happened? What should be different?"
              }
              rows={content ? 3 : 5}
              className="w-full resize-none"
            />

            {screenshot && (
              <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                <div className="relative shrink-0">
                  <img
                    src={screenshot.dataUrl}
                    alt="Screenshot of this page"
                    className="h-24 w-auto max-w-[96px] rounded-md border border-gray-200 object-cover object-top"
                  />
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    aria-label="Remove screenshot"
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-white shadow hover:bg-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="text-xs leading-snug text-muted-foreground">
                  A screenshot of this page will be included so we can see what
                  you saw. Tap the X to send without it.
                </p>
              </div>
            )}
          </div>

          <DrawerFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={!canSubmit || sending}
            >
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              {sending ? "Sending…" : "Send report"}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
